import { classNames } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorRulingField } from '@/msgdeskRuntime/contracts/editorModel'
import { useEditorFormModelSnapshot } from '../../EditorTemplateContract'
import { useEditorSurface, type EditorSkin } from '../../EditorSurfaceContext'

// Timers / schedule (legacy "ruling"). The native datetime controls consume the
// semantic model; EditorRoughFacade alone translates named fields to rough slots.

function setRulingModel(timeField: EditorRulingField, enabledField: EditorRulingField, unix: number) {
  dispatchEditorAction({ action: 'set-ruling', field: timeField, value: unix, reason: 'react-ruling:time' })
  dispatchEditorAction({ action: 'set-ruling', field: enabledField, value: unix > 0, reason: 'react-ruling:enabled' })
}

function setRulingFlag(field: EditorRulingField, on: boolean) {
  dispatchEditorAction({ action: 'set-ruling', field, value: on, reason: 'react-ruling:flag' })
}

function localDateTimeValue(unix: number | undefined): string {
  if (!unix) return ''
  const date = new Date(unix * 1000)
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}

function TimeField({
  label,
  hiddenName,
  checkName,
  timeField,
  enabledField,
  unix,
  enabled,
  classes,
}: {
  label: string
  hiddenName: string
  checkName: string
  timeField: EditorRulingField
  enabledField: EditorRulingField
  unix?: number
  enabled?: boolean
  classes: EditorSkin['settings']
}) {
  const clear = () => {
    setRulingModel(timeField, enabledField, 0)
  }

  // BEHAVIOUR: a picked date enables the rule and stores its unix seconds.
  const handlePick = (value: string) => {
    if (!value) {
      clear()
      return
    }
    setRulingModel(timeField, enabledField, Math.round(new Date(value).getTime() / 1000))
  }

  // BEHAVIOUR: unticking disables the rule (clears the date); ticking keeps it.
  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.currentTarget.checked) {
      clear()
      return
    }
    setRulingFlag(enabledField, true)
  }

  return (
    <div className={classNames('md-settings__time-row', classes.timeRow)}>
      <label className={classes.check}>
        <input type="checkbox" name={checkName} value="1" checked={enabled ?? false} onChange={handleToggle} />
        {label}
      </label>
      <input type="datetime-local" value={localDateTimeValue(unix)} className={classNames('md-settings__time-input', classes.timeInput)} onChange={event => handlePick(event.currentTarget.value)} />
      <input type="hidden" name={hiddenName} value={unix || ''} />
    </div>
  )
}

export function RulePanel() {
  const { skin } = useEditorSurface()
  const classes = skin.settings
  const ruling = useEditorFormModelSnapshot()?.ruling
  return (
    <fieldset className={classes.panel}>
      <legend className={classes.legend}>Show</legend>
      <TimeField label="Start:" hiddenName="time_start" checkName="is_rule_start" timeField="timeStart" enabledField="startEnabled" unix={ruling?.timeStart} enabled={ruling?.startEnabled} classes={classes} />
      {/* Legacy "( hide )" companion of the Start row: hide the desk entirely
          while it is time-locked instead of showing the availability notice. */}
      <label className={classes.check} title="Hide the desk while time-locked">
        <input
          type="checkbox"
          name="is_hide_time_locked"
          value="1"
          checked={ruling?.hideTimeLocked ?? false}
          onChange={event => dispatchEditorAction({ action: 'set-ruling', field: 'hideTimeLocked', value: event.currentTarget.checked, reason: 'react-ruling:hide-locked' })}
        />
        ( hide )
      </label>
      <TimeField label="Finish:" hiddenName="time_stop" checkName="is_rule_stop" timeField="timeStop" enabledField="stopEnabled" unix={ruling?.timeStop} enabled={ruling?.stopEnabled} classes={classes} />
      {/* Legacy "( and delete )" companion of the Finish row: more_settings[21],
          uncontrolled like the other advanced checkboxes (m31 binds its click). */}
      <label className={classes.check} title="Delete the msg when the show finishes">
        <input type="checkbox" name="is_to_remove" value="1" />
        ( and delete )
      </label>
    </fieldset>
  )
}
